/**
 * Per-drop AI generated texts preview – selectable, regenerable, apply to each.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Sparkles, Layers, RefreshCw, EyeOff, Languages } from 'lucide-react';

import { Button, LoadingSpinner, Tooltip } from '@/components/ui';
import {
  SMS_TEXT_MAX_LENGTH,
  isSmsTextOverLimit,
} from '@/features/campaign-texts/smsTextLimit';
import { useTextPromptGroups } from '../hooks/useTextPromptGroups';
import type { DropLabel } from './PerDropGroupSelector';
import { PreviewDevExactPromptButton } from './PreviewDevExactPromptButton';
import { PreviewTextItem } from './PreviewTextItem';
import type { TextPromptGroup } from '../types/campaign-texts.types';

interface GeneratedTextsPreviewPerDropProps {
  batches: string[][];
  /** Optional English translations per batch (null = missing). */
  translationsPerDrop?: (string | null)[][] | null;
  /** Group id per drop (for grouping when multiple groups). */
  groupIdsPerDrop?: string[];
  /** List name + id per drop (for display when grouping by group). */
  dropLabels?: DropLabel[];
  onSave: (batches: string[][]) => void;
  onDismiss: () => void;
  onRegenerate?: (dropIdx: number, indices: number[]) => void;
  onRegenerateAll?: (dropIndices: Array<{ dropIdx: number; indices: number[] }>) => void;
  /** Regenerate every slot whose original SMS body exceeds the segment limit (uses `batches`, not translations). */
  onRegenerateOverLength?: () => void;
  /** Regenerate only drops that failed (0 texts). */
  onRegenerateFailed?: (failedDropIndices: number[]) => void;
  onSaveAndHide?: (batches: string[][], unselectedTexts: string[]) => void;
  onTranslatePerDropAll?: (dropIdx: number) => void;
  onTranslatePerDropMissing?: (dropIdx: number) => void;
  /** Translates all missing across all drops (header-level). */
  onTranslateAllPerDrop?: () => void;
  isRegenerating?: boolean;
  isTranslating?: boolean;
  /** Dev: copy exact system + user prompt (first drop’s group when groups differ). */
  onDevCopyExactPrompt?: () => void | Promise<void>;
  isDevCopyExactPromptPending?: boolean;
  /** Per-drop uses Use & Hide only (no Apply). */
  useAndHideOnly?: boolean;
  /** Generation time in ms (admin/debug). */
  generationTimeMs?: number;
  /** When true, shows applied state (after Use & Hide). */
  applied?: boolean;
  /** When true, fills parent height so footer stays at bottom of card. */
  fillHeight?: boolean;
}

function getGroupName(gId: string, groups: TextPromptGroup[] | undefined): string {
  if (gId === 'master') return 'Default';
  return groups?.find((g) => g.id === gId)?.name ?? gId;
}

/** [groupId, dropIndices[]] for grouped display. */
function buildGroupedDrops(
  groupIdsPerDrop: string[],
  batchCount: number,
  groups: TextPromptGroup[] | undefined,
): Array<{ groupId: string; groupName: string; dropIndices: number[] }> {
  const byGroup = new Map<string, number[]>();
  for (let dropIdx = 0; dropIdx < batchCount; dropIdx++) {
    const gId = groupIdsPerDrop[dropIdx] || 'master';
    const arr = byGroup.get(gId) ?? [];
    arr.push(dropIdx);
    byGroup.set(gId, arr);
  }
  return Array.from(byGroup.entries()).map(([groupId, dropIndices]) => ({
    groupId,
    groupName: getGroupName(groupId, groups),
    dropIndices,
  }));
}

export function GeneratedTextsPreviewPerDrop({
  batches,
  translationsPerDrop,
  groupIdsPerDrop = [],
  dropLabels,
  onSave,
  onDismiss,
  onRegenerate,
  onRegenerateAll,
  onRegenerateOverLength,
  onRegenerateFailed,
  onSaveAndHide,
  onTranslatePerDropAll,
  onTranslatePerDropMissing,
  onTranslateAllPerDrop,
  isRegenerating = false,
  isTranslating = false,
  onDevCopyExactPrompt,
  isDevCopyExactPromptPending,
  useAndHideOnly = true,
  generationTimeMs,
  applied = false,
  fillHeight = false,
}: GeneratedTextsPreviewPerDropProps) {
  const { data: groups } = useTextPromptGroups();
  const containerRef = useRef<HTMLDivElement>(null);

  const hasMultipleGroups = useMemo(() => {
    const ids = new Set(groupIdsPerDrop.filter(Boolean).map((id) => id || 'master'));
    return ids.size > 1;
  }, [groupIdsPerDrop]);
  const groupedDrops = useMemo(
    () =>
      hasMultipleGroups ? buildGroupedDrops(groupIdsPerDrop, batches.length, groups) : null,
    [hasMultipleGroups, groupIdsPerDrop, batches.length, groups],
  );
  const [selected, setSelected] = useState<Map<number, Set<number>>>(() =>
    new Map(batches.map((b, di) => [di, new Set(b.map((_, i) => i))])),
  );
  const [showEnglish, setShowEnglish] = useState(false);

  const hasAnyTranslation = Boolean(
    translationsPerDrop?.some((batch) => batch?.some((t) => t != null)),
  );
  const totalMissing = useMemo(
    () =>
      batches.reduce(
        (acc, batch, di) =>
          acc +
          (translationsPerDrop?.[di]?.filter((t) => t == null).length ?? batch.length),
        0,
      ),
    [batches, translationsPerDrop],
  );
  const displayBatches = useMemo(
    () =>
      hasAnyTranslation && showEnglish
        ? batches.map((batch, di) =>
            batch.map((t, i) => (translationsPerDrop?.[di]?.[i] ?? t) as string),
          )
        : batches,
    [batches, translationsPerDrop, hasAnyTranslation, showEnglish],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (batches.length > 0 && el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [batches.length]);

  const toggleItem = useCallback((dropIdx: number, textIdx: number) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(dropIdx) ?? []);
      if (set.has(textIdx)) set.delete(textIdx);
      else set.add(textIdx);
      next.set(dropIdx, set);
      return next;
    });
  }, []);

  const toggleDropAll = useCallback(
    (dropIdx: number) => {
      setSelected((prev) => {
        const next = new Map(prev);
        const batch = batches[dropIdx];
        if (!batch) return next;
        const current = next.get(dropIdx) ?? new Set();
        const allSelected = current.size >= batch.length;
        next.set(dropIdx, allSelected ? new Set() : new Set(batch.map((_, i) => i)));
        return next;
      });
    },
    [batches],
  );

  const selectedBatches = useMemo(
    () =>
      batches.map((batch, di) => {
        const sel = selected.get(di) ?? new Set();
        return batch.filter((_, i) => sel.has(i));
      }),
    [batches, selected],
  );

  const totalSelected = selectedBatches.reduce((a, b) => a + b.length, 0);
  const totalTexts = batches.reduce((a, b) => a + b.length, 0);
  const showTranslateAllHeader = onTranslateAllPerDrop && totalMissing > 0;
  const unselectedTexts = useMemo(
    () =>
      batches.flatMap((batch, di) => {
        const sel = selected.get(di) ?? new Set();
        return batch.filter((_, i) => !sel.has(i));
      }),
    [batches, selected],
  );
  const totalUnselected = unselectedTexts.length;

  const handleSave = useCallback(() => {
    if (totalSelected === 0) return;
    onSave(selectedBatches);
  }, [selectedBatches, totalSelected, onSave]);

  const handleRegenerateDrop = useCallback(
    (dropIdx: number) => {
      const batch = batches[dropIdx];
      const sel = selected.get(dropIdx) ?? new Set();
      const indices = batch.map((_, i) => i).filter((i) => !sel.has(i));
      if (indices.length > 0 && onRegenerate) onRegenerate(dropIdx, indices);
    },
    [batches, selected, onRegenerate],
  );

  const handleRegenerateAll = useCallback(() => {
    const dropIndices = batches
      .map((batch, dropIdx) => {
        const sel = selected.get(dropIdx) ?? new Set();
        const indices = batch.map((_, i) => i).filter((i) => !sel.has(i));
        return { dropIdx, indices };
      })
      .filter((d) => d.indices.length > 0);
    if (dropIndices.length > 0 && onRegenerateAll) onRegenerateAll(dropIndices);
  }, [batches, selected, onRegenerateAll]);

  const failedDropIndices = useMemo(
    () => batches.map((b, i) => (b.length === 0 ? i : -1)).filter((i) => i >= 0),
    [batches],
  );
  const handleRegenerateFailed = useCallback(() => {
    if (failedDropIndices.length > 0 && onRegenerateFailed) onRegenerateFailed(failedDropIndices);
  }, [failedDropIndices, onRegenerateFailed]);

  const handleSaveAndHide = useCallback(() => {
    if (totalSelected === 0) return;
    onSaveAndHide?.(selectedBatches, unselectedTexts);
  }, [selectedBatches, totalSelected, unselectedTexts, onSaveAndHide]);

  const showRegenAll = !applied && onRegenerateAll && totalUnselected > 0;
  const hasOverLengthOriginal = useMemo(
    () => batches.some((batch) => batch.some((t) => isSmsTextOverLimit(t))),
    [batches],
  );
  const showRegenOver160 =
    !applied && Boolean(onRegenerateOverLength) && hasOverLengthOriginal;
  const showRegenFailed = !applied && onRegenerateFailed && failedDropIndices.length > 0;
  const showSaveAndHide = !applied && onSaveAndHide && totalSelected > 0;
  const showApply = !useAndHideOnly && !applied;

  const containerClass = fillHeight
    ? 'flex min-h-0 flex-1 flex-col overflow-hidden border-b border-border'
    : 'flex max-h-[min(480px,60vh)] min-h-[140px] flex-col overflow-hidden border-b border-border';

  const content = (
    <div ref={containerRef} className={containerClass}>
      <div className="sticky top-0 z-20 flex shrink-0 items-center justify-between gap-2 rounded-t-lg border-b border-border bg-background px-3 py-2 shadow-sm backdrop-blur-sm">
          <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Layers className="h-3 w-3 text-primary" />
            {applied ? 'Applied' : `Per drop (${totalSelected}/${totalTexts} selected)`}
          </span>
          <div className="flex items-center gap-2">
            {showTranslateAllHeader && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 text-[10px]"
                onClick={onTranslateAllPerDrop}
                disabled={isTranslating}
              >
                {isTranslating ? (
                  <LoadingSpinner className="h-3 w-3" />
                ) : (
                  <Languages className="h-3 w-3" />
                )}
                {totalMissing === totalTexts ? 'Translate all' : `Translate ${totalMissing} missing`}
              </Button>
            )}
            {hasAnyTranslation && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 text-[10px]"
                onClick={() => setShowEnglish((v) => !v)}
              >
                <Languages className="h-3 w-3" />
                {showEnglish ? 'Show original' : 'Show English'}
              </Button>
            )}
            {onDevCopyExactPrompt && (
              <PreviewDevExactPromptButton
                onCopy={onDevCopyExactPrompt}
                pending={isDevCopyExactPromptPending}
              />
            )}
            {generationTimeMs != null && (
              <span className="text-[10px] text-muted-foreground" title="Generation time (debug)">
                {generationTimeMs < 1000 ? `${generationTimeMs}ms` : `${(generationTimeMs / 1000).toFixed(1)}s`}
              </span>
            )}
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDismiss}>
              <X className="h-3 w-3" />
            </Button>
          </div>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-2 py-1.5 scrollbar-thin">
        {(groupedDrops ?? batches.map((_, di) => ({ groupId: '', groupName: '', dropIndices: [di] }))).map(
          (group) => (
            <div key={group.groupId || `drop-${group.dropIndices[0]}`} className="space-y-2">
              {group.groupName && (
                <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  <Layers className="h-3 w-3 shrink-0" />
                  {group.groupName}
                </div>
              )}
              {group.dropIndices.map((dropIdx) => {
                const batch = displayBatches[dropIdx];
                const origBatch = batches[dropIdx];
                if (!batch || !origBatch) return null;
                const sel = selected.get(dropIdx) ?? new Set();
                const unselectedCount = batch.length - sel.size;
                const showRegen = !applied && onRegenerate && unselectedCount > 0;
                const showDropActions = !applied;
                const dropTrans = translationsPerDrop?.[dropIdx];
                const missingInDrop = dropTrans?.filter((t) => t == null).length ?? batch.length;
                const showTranslateDropAll =
                  (onTranslatePerDropAll || onTranslatePerDropMissing) &&
                  (!dropTrans || missingInDrop === batch.length);
                const showTranslateDropMissing =
                  onTranslatePerDropMissing && dropTrans && missingInDrop > 0 && missingInDrop < batch.length;
                const dl = dropLabels?.[dropIdx];
                const dropLabel = dl ? (
                  <span className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-foreground" title={dl.label}>
                      {dl.label}
                    </span>
                    <Tooltip content={dl.id} side="top">
                      <span className="shrink-0 rounded bg-muted/60 px-1 py-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
                        ID
                      </span>
                    </Tooltip>
                  </span>
                ) : (
                  `Drop ${dropIdx + 1}`
                );
                return (
                  <div key={dropIdx} className="rounded-lg border border-border bg-muted/10 p-2">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <span className="flex min-w-0 items-center gap-1.5 text-[11px]">
                        <Sparkles className="h-3 w-3 shrink-0 text-muted-foreground" />
                        {dropLabel}
                        <span className="shrink-0 text-muted-foreground">
                          — {applied ? batches[dropIdx]!.length : `${sel.size}/${batches[dropIdx]!.length} selected`}
                        </span>
                      </span>
                {showDropActions && (
                <div className="flex flex-wrap items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-1.5 text-[10px]"
                    onClick={() => toggleDropAll(dropIdx)}
                  >
                    {sel.size >= batch.length ? 'Deselect all' : 'Select all'}
                  </Button>
                  {showTranslateDropAll && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 gap-0.5 px-1.5 text-[10px]"
                      onClick={() => onTranslatePerDropAll?.(dropIdx)}
                      disabled={isTranslating}
                    >
                      {isTranslating ? <LoadingSpinner className="h-2.5 w-2.5" /> : <Languages className="h-2.5 w-2.5" />}
                      Translate
                    </Button>
                  )}
                  {showTranslateDropMissing && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 gap-0.5 px-1.5 text-[10px]"
                      onClick={() => onTranslatePerDropMissing?.(dropIdx)}
                      disabled={isTranslating}
                    >
                      {isTranslating ? <LoadingSpinner className="h-2.5 w-2.5" /> : <Languages className="h-2.5 w-2.5" />}
                      Translate {missingInDrop}
                    </Button>
                  )}
                  {showRegen && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 gap-0.5 px-1.5 text-[10px]"
                      onClick={() => handleRegenerateDrop(dropIdx)}
                      disabled={isRegenerating}
                    >
                      {isRegenerating ? (
                        <LoadingSpinner className="h-2.5 w-2.5" />
                      ) : (
                        <RefreshCw className="h-2.5 w-2.5" />
                      )}
                      Regen {unselectedCount}
                    </Button>
                  )}
                </div>
                )}
              </div>
              <div className="space-y-1">
                <AnimatePresence>
                  {batch.map((text, textIdx) => (
                    <PreviewTextItem
                      key={`${dropIdx}-${textIdx}-${text.slice(0, 20)}`}
                      text={text}
                      lengthSourceText={origBatch[textIdx]}
                      index={textIdx}
                      isSelected={applied || sel.has(textIdx)}
                      onToggle={applied ? () => {} : () => toggleItem(dropIdx, textIdx)}
                      lengthLimit={SMS_TEXT_MAX_LENGTH}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          );
        })}
            </div>
        ))}
      </div>
      <div className="z-20 shrink-0 space-y-1.5 rounded-b-lg border-t border-border bg-background px-3 pt-2 pb-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex gap-1.5">
          {showApply && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={handleSave}
              disabled={totalSelected === 0}
            >
              <Check className="h-3 w-3" />
              Apply {totalSelected}
            </Button>
          )}
          {showRegenFailed && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={handleRegenerateFailed}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <LoadingSpinner className="h-3 w-3" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Regen failed ({failedDropIndices.length})
            </Button>
          )}
          {showRegenOver160 && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={() => onRegenerateOverLength?.()}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <LoadingSpinner className="h-3 w-3" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Regen &gt;160
            </Button>
          )}
          {showRegenAll && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={handleRegenerateAll}
              disabled={isRegenerating}
            >
              {isRegenerating ? (
                <LoadingSpinner className="h-3 w-3" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Regen {totalUnselected}
            </Button>
          )}
          {showSaveAndHide && (
            <Button
              size="sm"
              className="h-7 flex-1 gap-1 text-xs"
              onClick={handleSaveAndHide}
              disabled={totalSelected === 0}
            >
              <EyeOff className="h-3 w-3" />
              Use & Hide
            </Button>
          )}
          {applied && (
            <Button variant="outline" size="sm" className="h-7 flex-1 gap-1 text-xs" onClick={onDismiss}>
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={fillHeight ? 'flex min-h-0 flex-1 flex-col overflow-hidden w-full' : 'w-full'}
    >
      {content}
    </motion.div>
  );
}
